/**
 * Scroll-into-view reveal for the support hero section.
 * Animates once when ~20px of the section has scrolled into view, then stays visible without replaying.
 */
class SupportHero extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {IntersectionObserver | undefined} */
  #observer;

  #setCompleted() {
    this.classList.remove('support-hero--idle', 'is-revealed', 'is-animating', 'is-resetting');
    this.classList.add('support-hero--completed');
  }

  #hasCompleted() {
    return this.classList.contains('support-hero--completed');
  }

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const stage = this.querySelector('.support-hero__stage');

    if (!(stage instanceof HTMLElement)) return;

    if (this.#hasCompleted()) {
      this.#setCompleted();
      return;
    }

    this.classList.add('support-hero--idle');

    const reveal = () => {
      if (this.#hasCompleted()) return;
      if (this.classList.contains('is-animating')) return;

      this.classList.remove('is-revealed', 'is-resetting', 'support-hero--idle');
      this.classList.add('is-animating');
      void this.offsetWidth;

      window.requestAnimationFrame(() => {
        this.classList.add('is-revealed');

        const cloudsDuration = Number(this.dataset.cloudsDuration) || 500;
        const contentDelay = Number(this.dataset.contentDelay) || 500;
        const contentDuration = Number(this.dataset.contentDuration) || 200;
        const totalDuration = Math.max(cloudsDuration, contentDelay + contentDuration);

        window.setTimeout(() => {
          this.#setCompleted();
        }, totalDuration + 50);
      });
    };

    // Reveal once ~20px of the section has scrolled into view from the bottom.
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            reveal();
            this.#observer?.disconnect();
            break;
          }
        }
      },
      { rootMargin: '0px 0px -20px 0px', threshold: 0 }
    );

    this.#observer.observe(this);

    if (window.Shopify?.designMode) {
      document.addEventListener(
        'shopify:section:select',
        (event) => {
          if (!(event.target instanceof HTMLElement)) return;
          if (!event.target.contains(this)) return;

          this.classList.remove('support-hero--completed', 'is-revealed', 'is-animating');
          this.classList.add('support-hero--idle', 'is-resetting');
          void this.offsetWidth;
          this.classList.remove('is-resetting');

          window.requestAnimationFrame(() => reveal());
        },
        { signal }
      );
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
  }
}

if (!customElements.get('support-hero')) {
  customElements.define('support-hero', SupportHero);
}
